import { useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { authService } from '../services/authService';
import { getMenuItemsForRole, roleLabels } from '../config/menuItems';
import { env } from '../config/env';

const DRAWER_WIDTH = 232;

const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = authService.getCurrentUser();
  const menuItems = useMemo(() => getMenuItemsForRole(currentUser?.role), [currentUser?.role]);

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const pageTitle = useMemo(() => {
    const match = menuItems.find(
      (item) => location.pathname === item.path || location.pathname.startsWith(`${item.path}/`),
    );
    return match?.label || env.appTitle;
  }, [location.pathname, menuItems]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: (t) => t.piper?.appBg ?? '#f5f6f8' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: (t) => t.piper?.navRail ?? '#263238',
            color: '#fff',
            borderRight: 'none',
          },
        }}
      >
        <Toolbar sx={{ px: 2 }}>
          <Typography variant="h5" sx={{ color: '#fff', letterSpacing: '-0.02em' }}>
            RatesBoard
          </Typography>
        </Toolbar>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
        <List sx={{ px: 1, py: 1.5 }}>
          {menuItems.map((item) => {
            const selected = location.pathname === item.path || location.pathname.startsWith(`${item.path}/`);
            const Icon = item.icon;
            return (
              <ListItemButton
                key={item.path}
                selected={selected}
                onClick={() => navigate(item.path)}
                sx={{
                  mb: 0.5,
                  color: 'rgba(255,255,255,0.78)',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(255,255,255,0.12)',
                    color: '#fff',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
                  },
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                  <Icon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItemButton>
            );
          })}
        </List>
      </Drawer>

      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <Box
          component="header"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1.25,
            backgroundColor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h4" component="h1">
            {pageTitle}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="subtitle2">{currentUser?.name || currentUser?.email}</Typography>
              <Typography variant="caption" color="text.secondary">
                {roleLabels[currentUser?.role] || currentUser?.role}
              </Typography>
            </Box>
            <IconButton onClick={handleLogout} aria-label="Sign out" size="small">
              <LogoutOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <Box component="main" sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          <Box key={location.pathname} className="page-fade-in">
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AppLayout;
